import { getConfluencePageAriFromContentRef } from '@databases/connections/utils/ariHelper';
import { getFlagValue } from '@databases/feature-flags/client';
import { ContentFieldToDisplayedDetailType } from '@databases/model/field';
import { DefaultRootState } from '@databases/redux';
import { loadContentExcerpt } from '@databases/redux/connections/actions/loadExcerpt';
import { loadContentVersionComment } from '@databases/redux/connections/actions/loadVersionComment';
import {
  getContentForReference,
  selectContentOptional,
} from '@databases/redux/connections/selectors';
import { ObjectMode, Optional } from '@databases/types';
import { Content, isAnonymous } from '@databases/types/confluence';
import {
  DatabaseField,
  DatabaseFieldType,
  DisplayedDetailType,
  EntryWithContext,
  FieldWithContext,
  RawValueWithContext,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isString } from 'lodash-es';

import { serializeUserReference } from '../../confluence/utils';
import { parseHTML } from '../../lib/dom';
import { loadContentById, loadExcerpt } from '../../redux/content/actions';
import {
  selectContentByIdOptional,
  selectContentRefOptional,
  selectExcerpt,
} from '../../redux/content/selectors';
import { encodeHtml } from '../../utils';
import { FieldType } from '../field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase,
} from '../utils';

import { getEncodedDate, stringToDate } from './date';
import {
  filterValidPageLinkValue,
  isExistingPageLinkValue,
  normalizePageLinkValue,
} from './pageLink';

/**
 * Returns the page link field in the same database that the page status field references.
 */
function getReferencedPageField({
  database,
  field,
}: FieldWithContext<DatabaseFieldType, ObjectMode>):
  | DatabaseField<DatabaseFieldType.PAGE_LINK>
  | undefined {
  const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
  if (referencedField?.type !== DatabaseFieldType.PAGE_LINK) {
    return undefined;
  } else {
    return referencedField as DatabaseField<DatabaseFieldType.PAGE_LINK>;
  }
}

/**
 * Returns the content objects that the referenced page link field references and that are currently loaded into the Redux store.
 */
export function getReferencedPageContent<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: Optional<
    FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
    'entry'
  >,
  state: State
): Array<Content<'page'>> {
  if (!entry) {
    return [];
  }
  const referencedField = getReferencedPageField({ database, field });
  const referencedValue =
    referencedField && getConvertedDatabaseValue(database, referencedField, entry, state);
  return normalizePageLinkValue(referencedValue?.value ?? [], state).flatMap(
    (contentRef) => {
      const resolvedContentRef = isExistingPageLinkValue(contentRef)
        ? selectContentRefOptional(state, { contentRef })
        : undefined;
      const content =
        resolvedContentRef &&
        selectContentByIdOptional(state, { contentId: resolvedContentRef.contentId });
      return content && content.type === 'page' ? [content] : [];
    }
  );
}

function getVersion(versionNumber: number | undefined, versionComment?: string): string {
  if (versionComment) {
    return `<p><b>v.${versionNumber ?? ''}</b> - ${encodeHtml(versionComment).replace(
      /\r|\n|\r\n/g,
      '<br/>'
    )}</p>`;
  } else {
    return `<p><b>v.${versionNumber ?? ''}</b></p>`;
  }
}

function renderExportView(
  {
    database,
    field,
    entry,
  }: Optional<RawValueWithContext<DatabaseFieldType.PAGE_DETAILS, ObjectMode>, 'entry'>,
  state: DefaultRootState
): string {
  const isOneWaySyncingEnabled = getFlagValue(
    'databases.frontend.one-way-syncing.confluence',
    false
  );

  if (isOneWaySyncingEnabled) {
    return getContentForReference(database, field, entry, state)
      .flatMap((content) => {
        const contentField =
          field.settings.contentField || field.settings.displayedDetail;
        return content['data'][contentField]?.text ?? '';
      })
      .join('<br/>');
  }

  if (!isOneWaySyncingEnabled) {
    return getReferencedPageContent({ database, field, entry }, state)
      .flatMap((content) => {
        switch (field.settings.displayedDetail) {
          case DisplayedDetailType.CREATED_AT: {
            const value = content.history?.createdDate;
            if (isString(value)) {
              const dateString = stringToDate(value)!;
              return getEncodedDate(dateString);
            } else {
              return '';
            }
          }
          case DisplayedDetailType.LAST_UPDATED_AT: {
            const value = content.history?.lastUpdated?.when;
            if (isString(value)) {
              const dateString = stringToDate(value)!;
              return getEncodedDate(dateString);
            } else {
              return '';
            }
          }
          case DisplayedDetailType.CREATED_BY: {
            const user = content.history?.createdBy;
            if (user) {
              return !isAnonymous(user)
                ? serializeUserReference(user.accountId, true)
                : user.displayName;
            } else {
              return '';
            }
          }
          case DisplayedDetailType.LAST_UPDATED_BY: {
            const user = content.history?.lastUpdated?.by;
            if (user) {
              return !isAnonymous(user)
                ? serializeUserReference(user.accountId, true)
                : user.displayName;
            } else {
              return '';
            }
          }
          case DisplayedDetailType.CURRENT_VERSION: {
            const versionComment = encodeHtml(
              content.history?.lastUpdated?.message ?? ''
            ).replace(/\r|\n|\r\n/g, '<br/>');
            const versionNumber = content.history?.lastUpdated?.number;
            return getVersion(
              versionNumber,
              field.settings.includeVersionComment ? versionComment : undefined
            );
          }
          case DisplayedDetailType.EXCERPT:
            return selectExcerpt(state, { contentId: content.id })?.html ?? '';
        }
      })
      .join('<br/>');
  }

  return '';
}

function getStringRepresentation(
  {
    database,
    field,
    entry,
  }: Optional<RawValueWithContext<DatabaseFieldType.PAGE_DETAILS, ObjectMode>, 'entry'>,
  state: DefaultRootState | undefined
): string {
  if (!state) {
    return '';
  }
  const isOneWaySyncingEnabled = getFlagValue(
    'databases.frontend.one-way-syncing.confluence',
    false
  );

  if (isOneWaySyncingEnabled) {
    return getContentForReference(database, field, entry, state)
      .flatMap((content) => {
        const contentField =
          field.settings.contentField || field.settings.displayedDetail;
        return content['data'][contentField]?.text ?? '';
      })
      .join('\n');
  }

  if (!isOneWaySyncingEnabled) {
    return getReferencedPageContent({ database, field, entry }, state)
      .flatMap((content) => {
        switch (field.settings.displayedDetail) {
          case DisplayedDetailType.CREATED_AT:
            return content.history?.createdDate ?? '';
          case DisplayedDetailType.LAST_UPDATED_AT:
            return content.history?.lastUpdated?.when ?? '';
          case DisplayedDetailType.CREATED_BY:
            return content.history?.createdBy.displayName ?? '';
          case DisplayedDetailType.LAST_UPDATED_BY:
            return content.history?.lastUpdated?.by.displayName ?? '';
          case DisplayedDetailType.CURRENT_VERSION: {
            const versionComment = content.history?.lastUpdated?.message;
            const versionNumber = content.history?.lastUpdated?.number;
            return parseHTML(
              getVersion(
                versionNumber,
                field.settings.includeVersionComment ? versionComment : undefined
              )
            ).text();
          }
          case DisplayedDetailType.EXCERPT:
            return selectExcerpt(state, { contentId: content.id })?.text ?? '';
        }
      })
      .join('\n');
  }

  return '';
}

const pageDetailsFieldType: FieldType<DatabaseFieldType.PAGE_DETAILS> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  supportedImportTypes: [],

  serializeValue: () => undefined,

  deserializeValue: () => ({}),

  getInitialValue: () => ({}),

  isEmptyValue: (context, state) => getStringRepresentation(context, state) === '',

  isEqualValue: (context1, context2, state) => {
    if (!state) {
      return true;
    }

    return renderExportView(context1, state) === renderExportView(context2, state);
  },

  renderExportView,

  getStringRepresentation,

  loadDependencies: (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase }
  ) => {
    const referencedContent = selector((state) => {
      const _db = selectDatabase(state, { contentId });
      const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
      const referencedField = database && getReferencedPageField({ database, field });
      const entry =
        database && entryId ? getDatabaseEntryOptional(database, entryId) : undefined;
      const value =
        database &&
        referencedField &&
        entry &&
        getConvertedDatabaseValue(database, referencedField, entry, state);
      return (
        value &&
        filterValidPageLinkValue(value.value).filter((contentRef) =>
          isExistingPageLinkValue(contentRef)
        )
      );
    });

    const isOneWaySyncingEnabled = getFlagValue(
      'databases.frontend.one-way-syncing.confluence',
      false
    );

    if (isOneWaySyncingEnabled) {
      const connectionContent = selector((state) => {
        return referencedContent?.map((content) => {
          if (referencedContent && 'contentId' in content) {
            const ari = getConfluencePageAriFromContentRef(content);
            if (ari) {
              return selectContentOptional(state.connections, ari);
            }
          }

          return null;
        });
      });

      for (const content of connectionContent ?? []) {
        if (content) {
          if (field.settings.contentField === DisplayedDetailType.EXCERPT) {
            dispatch(loadContentExcerpt({ ari: content.ari }));
          }

          if (
            field.settings.contentField ===
              ContentFieldToDisplayedDetailType.VERSION_NUMBER &&
            field.settings.includeVersionComment
          ) {
            dispatch(loadContentVersionComment({ ari: content.ari }));
          }
        }
      }
    }

    if (!isOneWaySyncingEnabled) {
      for (const contentRef of referencedContent ?? []) {
        if (contentRef.contentId) {
          dispatch(loadContentById(contentRef.contentId));

          const resolvedContentRef = selector((state) =>
            selectContentRefOptional(state, { contentRef })
          );

          if (
            resolvedContentRef &&
            (field.settings.displayedDetail === DisplayedDetailType.EXCERPT ||
              field.settings.contentField === DisplayedDetailType.EXCERPT)
          ) {
            dispatch(loadExcerpt(resolvedContentRef));
          }
        }
      }
    }
  },

  noDefaultValue: true,
};

export default pageDetailsFieldType;
